import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";
import { toast } from "sonner";

export default function AdminUsers() {
  const { lang } = useApp();
  const tr = t[lang];
  const isAr = lang === "ar";
  
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "user"
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // توليد كلمة مرور عشوائية
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const loadUsers = async () => {
    try {
      const userList = await base44.entities.User.list();
      setUsers(userList);
    } catch (err) {
      console.error('Error loading users:', err);
      toast.error(isAr ? "فشل تحميل المستخدمين" : "Failed to load users");
    }
  };

  // تحميل المستخدمين عند فتح الصفحة
  useEffect(() => {
    loadUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== Form Submit Started ===');
    console.log('Form data:', formData);
    
    // التحقق من أن جميع الحقول مطلوبة
    if (!formData.full_name || !formData.email || !formData.password) {
      console.log('Validation failed: missing fields');
      toast.error(isAr ? "يرجى ملء جميع الحقول" : "Please fill all fields");
      return;
    }
    
    // التحقق من صحة البريد الإلكتروني
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      console.log('Validation failed: invalid email');
      toast.error(isAr ? "البريد الإلكتروني غير صحيح" : "Invalid email format");
      return;
    }

    console.log('Validation passed, starting API call...');
    setLoading(true);
    try {
      console.log('=== Starting user creation ===');
      console.log('Form data:', formData);
      
      // إنشاء المستخدم مع كلمة المرور
      console.log('Calling createUser function with password...');
      const createResponse = await base44.functions.invoke('createUser', {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        password: formData.password // تمرير كلمة المرور
      });

      console.log('Create user response:', createResponse);
      console.log('Response data:', createResponse?.data);
      console.log('Response status:', createResponse?.status);

      // التحقق من نجاح العملية - الـ response يأتي مباشرة في data
      const responseData = createResponse?.data;
      
      if (!responseData) {
        console.error('No response data received');
        throw new Error('لا توجد استجابة من الخادم');
      }
      
      // التعامل مع مختلف أشكال الاستجابة
      if (responseData.error) {
        console.error('API returned error:', responseData.error);
        throw new Error(responseData.error === 'Email already exists' 
          ? (isAr ? 'البريد الإلكتروني مستخدم مسبقاً' : 'Email already exists')
          : responseData.error);
      }
      
      if (responseData.success !== true && !responseData.user) {
        console.error('Unexpected response format:', responseData);
        throw new Error(isAr ? 'فشل إنشاء المستخدم' : 'Failed to create user');
      }
      
      console.log('✅ User created successfully:', responseData.user || responseData);

      // استخدام الدومين الرسمي - إرسال كلمة المرور مباشرة
      const baseUrl = 'https://moon-laundry.com';
      const loginUrl = `${baseUrl}/admin/login`;
      
      console.log('Sending welcome email with password to:', formData.email);
      const subject = isAr ? "بيانات الدخول - لوحة تحكم مغاسل القمر الأبيض" : "Login Credentials - White Moon Laundry Admin Panel";
      const body = `
        <div style="font-family: Cairo, sans-serif; direction: ${isAr ? 'rtl' : 'ltr'}; text-align: ${isAr ? 'right' : 'left'};">
          <h2 style="color: #1E5FA8;">${isAr ? 'مرحباً ' + formData.full_name : 'Hello ' + formData.full_name}</h2>
          <p>${isAr ? 'تم إنشاء حسابك للوصول إلى لوحة التحكم' : 'Your account has been created to access the admin panel'}</p>
          
          <div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0; border: 2px solid #1E5FA8;">
            <h3 style="margin: 0 0 15px 0; color: #1E5FA8;">${isAr ? '📋 بيانات الدخول:' : '📋 Login Credentials:'}</h3>
            <p style="margin: 10px 0;"><strong>${isAr ? 'البريد الإلكتروني:' : 'Email:'}</strong> ${formData.email}</p>
            <p style="margin: 10px 0;"><strong>${isAr ? 'كلمة المرور:' : 'Password:'}</strong> <span style="background: #fff; padding: 5px 10px; border-radius: 4px; font-family: monospace;">${formData.password}</span></p>
            <p style="margin: 10px 0;"><strong>${isAr ? 'الصلاحية:' : 'Role:'}</strong> ${formData.role === 'admin' ? (isAr ? 'مدير' : 'Admin') : (isAr ? 'مستخدم' : 'User')}</p>
          </div>
          
          <a href="${loginUrl}" style="display: inline-block; background: #1E5FA8; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; margin: 20px 0; font-weight: bold; font-size: 16px;">
            ${isAr ? '🔑 تسجيل الدخول الآن' : '🔑 Login Now'}
          </a>
          
          <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin: 20px 0; border: 1px solid #ffc107;">
            <p style="margin: 0; color: #856404;">
              <strong>${isAr ? '💡 ملاحظة:' : '💡 Note:'}</strong>
              ${isAr ? ' يمكنك تغيير كلمة المرور بعد تسجيل الدخول من الإعدادات' : ' You can change your password after login from settings'}
            </p>
          </div>
          
          <p style="color: #666; font-size: 14px; margin-top: 20px;">${isAr ? 'شكراً لاستخدامكم مغاسل القمر الأبيض' : 'Thank you for using White Moon Laundry'}</p>
        </div>
      `;

      console.log('Sending email via Resend...');
      const emailResult = await base44.functions.invoke('sendOtpEmail', {
        email: formData.email,
        subject: subject,
        html: body,
        lang: lang
      });
      
      console.log('Email result:', emailResult);
      console.log('Email sent successfully:', emailResult);
      
      // عرض تفاصيل الإيميل للمستخدم
      const emailSuccess = emailResult?.data?.success || emailResult?.data?.id;
      console.log('📧 Email ID:', emailResult?.data?.id);
      console.log('📧 Email Status:', emailSuccess ? 'SENT' : 'FAILED');
      
      if (!emailSuccess) {
        console.error('Email sending failed:', emailResult);
        throw new Error(isAr ? 'فشل إرسال الإيميل' : 'Failed to send email');
      }

      toast.success(
        isAr 
          ? "✅ تم إنشاء الحساب\n📧 تم إرسال البريد إلى: " + formData.email
          : "✅ Account created\n📧 Email sent to: " + formData.email
      );
      setFormData({ full_name: "", email: "", password: "", role: "user" });
      await loadUsers();
    } catch (err) {
      console.error('Error adding user:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // رسالة خطأ أكثر تحديداً
      let errorMessage = err.message || (isAr ? "فشل إضافة المستخدم" : "Failed to add user");
      
      if (err.response?.status === 403) {
        errorMessage = isAr ? "❌ ليس لديك صلاحية لإضافة مستخدمين" : "❌ You don't have permission to add users";
      } else if (err.response?.status === 409) {
        errorMessage = isAr ? "❌ البريد الإلكتروني مستخدم مسبقاً" : "❌ Email already exists";
      } else if (err.response?.status === 400) {
        errorMessage = isAr ? "❌ بيانات غير صحيحة" : "❌ Invalid data";
      } else if (err.response?.status === 500) {
        errorMessage = isAr ? "❌ خطأ في الخادم - تحقق من Console" : "❌ Server error - check Console";
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-black text-foreground">
              {isAr ? "👥 إدارة المستخدمين" : "👥 User Management"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isAr ? "إضافة مستخدمين جدد للوحة التحكم" : "Add new users to the admin panel"}
            </p>
          </div>

          {/* Add User Form */}
          <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
            <h3 className="font-black text-lg text-foreground mb-4 flex items-center gap-2">
              <span>{isAr ? "➕ إضافة مستخدم جديد" : "➕ Add New User"}</span>
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">
                  {isAr ? "الاسم الكامل" : "Full Name"}
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground"
                  placeholder={isAr ? "مثال: أحمد محمد" : "e.g. John Doe"}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">
                  {isAr ? "البريد الإلكتروني" : "Email Address"}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground"
                  placeholder="user@example.com"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">
                  {isAr ? "كلمة المرور المؤقتة" : "Temporary Password"}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground"
                      placeholder={isAr ? "أدخل كلمة المرور" : "Enter password"}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, password: generatePassword() })}
                    className="px-4 py-2.5 bg-secondary text-secondary-foreground font-bold rounded-xl hover:bg-secondary/80 transition-colors whitespace-nowrap"
                    disabled={loading}
                  >
                    🎲 {isAr ? "توليد" : "Generate"}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {isAr ? "يمكنك توليد كلمة مرور عشوائية أو إدخالها يدوياً" : "Generate a random password or enter manually"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">
                  {isAr ? "صلاحية المستخدم" : "User Role"}
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground"
                  disabled={loading}
                >
                  <option value="user">{isAr ? "مستخدم عادي" : "Regular User"}</option>
                  <option value="admin">{isAr ? "مدير (Admin)" : "Admin"}</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-primary text-primary-foreground font-black rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60"
              >
                {loading ? (isAr ? "⌛ جاري الإضافة..." : "⌛ Adding...") : (isAr ? "➕ إضافة المستخدم" : "➕ Add User")}
              </button>
            </form>
          </div>

          {/* Users List */}
          <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-black text-foreground">{isAr ? "المستخدمون الحاليون" : "Current Users"}</h3>
              <button onClick={loadUsers} className="px-3 py-1.5 text-sm font-medium hover:bg-secondary rounded-lg transition-colors">
                🔄 {tr.refresh}
              </button>
            </div>
            <div className="divide-y divide-border">
              {users.map(user => (
                <div key={user.id} className="p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-lg">
                      {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <p className="font-bold text-foreground">{user.full_name || "-"}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    user.role === "admin" 
                      ? "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400" 
                      : "bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400"
                  }`}>
                    {user.role === "admin" ? (isAr ? "مدير" : "Admin") : (isAr ? "مستخدم" : "User")}
                  </span>
                </div>
              ))}
              {users.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  <p className="text-4xl mb-2">👥</p>
                  <p>{isAr ? "لا يوجد مستخدمين حالياً" : "No users yet"}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}